import React from "react";
import { WarningOctagon, WarningCircle, Info } from "@phosphor-icons/react";
import {
  AlertDialog, AlertDialogAction, AlertDialogContent,
  AlertDialogDescription, AlertDialogHeader, AlertDialogTitle,
} from "./ui/alert-dialog";

// v0.6.8 — shared centered-popup primitive for the message-uniformity
// pass. Before this, the same class of message (filing-blocking gate,
// "your save didn't actually apply", a compliance-risk disclosure)
// was presented completely differently depending on which screen
// happened to render it: a toast that auto-dismisses in the corner, a
// colored banner easy to scroll past, or an explanation hidden behind
// a hover tooltip. Every operator-facing message that genuinely needs
// to interrupt (blocks filing, means a prior action silently didn't
// take effect, or is a real compliance-risk disclosure) now goes
// through this one component instead of each screen inventing its
// own presentation. Built on the same AlertDialog primitive already
// used by Freeze/Supersede/QueuePosition, so it's visually identical
// to those, not a new fourth style.
//
// severity drives icon + accent color:
//   "error"   (rose)   — filing is blocked / a write silently failed
//   "warning" (amber)  — compliance-risk disclosure, needs review
//   "info"    (teal)   — informational but still deserves an interrupt
//
// `actions` defaults to a single dismiss button. Pass an array of
// {label, onClick, testid, variant} to offer more (e.g. "Review now"
// + "Dismiss") — variant "primary" gets the solid accent button,
// anything else gets the plain bordered style QueuePositionDialog's
// Cancel button already uses.
const SEVERITY = {
  error: {
    Icon: WarningOctagon,
    iconClass: "text-rose-600 dark:text-rose-400",
    primaryClass: "bg-rose-600 hover:bg-rose-700 text-white",
  },
  warning: {
    Icon: WarningCircle,
    iconClass: "text-amber-600 dark:text-amber-400",
    primaryClass: "bg-amber-600 hover:bg-amber-700 text-white",
  },
  info: {
    Icon: Info,
    iconClass: "text-teal-600 dark:text-teal-400",
    primaryClass: "bg-teal-600 hover:bg-teal-700 text-white",
  },
};

export const BlockingMessageDialog = ({
  open, onOpenChange, severity = "warning", title, testid, actions, children,
}) => {
  const { Icon, iconClass, primaryClass } = SEVERITY[severity] || SEVERITY.warning;
  const resolvedActions = actions && actions.length
    ? actions
    : [{ label: "OK", onClick: () => onOpenChange?.(false), variant: "primary" }];

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent data-testid={testid} className="bg-white dark:bg-slate-900 max-w-lg">
        <AlertDialogHeader className="items-center text-center">
          <Icon size={40} weight="duotone" className={`${iconClass} mb-2`} />
          <AlertDialogTitle className="text-lg">{title}</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-2 text-sm text-slate-600 dark:text-slate-300 text-left">
              {children}
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <div className="flex items-center justify-center gap-2 flex-wrap pt-2">
          {resolvedActions.map((a) => (
            <AlertDialogAction
              key={a.label}
              data-testid={a.testid}
              onClick={a.onClick}
              className={a.variant === "primary"
                ? primaryClass
                : "bg-transparent text-slate-500 dark:text-slate-400 border border-slate-300 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800"}
            >
              {a.label}
            </AlertDialogAction>
          ))}
        </div>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default BlockingMessageDialog;
